import {
  PAYMENT_ARCHITECTURES,
  PAYMENT_METHODS,
  isPaymentMethodEnabled,
  paymentMethodName,
} from '../../methods'
import {
  PAYMENT_ADVERTISING_IDS,
  PAYMENT_METHODS_ADVERTISING,
} from '../config/advertising'
import { PAYMENT_ADVERTISING_TYPES } from '../config/constants'

/**
 * @typedef {Object} PaymentAdvertisingOptions
 * @property {Object} rootGetters
 */

/**
 * TODO [PAYIN-2614] Remove this when the Paypal AB test is over
 * @param {PaymentMethod[]} paymentMethods
 * @param {PaymentAdvertisingOptions} options
 */
const filterABTestedPaymentAdvertising = (paymentMethods, options) => {
  if (
    !paymentMethods.find(
      ({ bmCode, architecture }) =>
        bmCode === PAYMENT_METHODS.PAYPAL &&
        architecture === PAYMENT_ARCHITECTURES.FINPAY,
    )
  ) {
    // We don't want to show the Paypal advertising for Paypal legacy.
    return Object.keys(PAYMENT_METHODS_ADVERTISING).filter(
      (advertisingId) => PAYMENT_ADVERTISING_IDS.PAYPAL !== advertisingId,
    )
  }

  const paypalPaymentAdvertisingExperiment =
    options.rootGetters?.['flags/getExperiments']?.paypalPaymentAdvertising

  switch (paypalPaymentAdvertisingExperiment) {
    case 'paypalBanners':
      return Object.keys(PAYMENT_METHODS_ADVERTISING)
    case 'noPaypalBanners':
    default:
      return Object.keys(PAYMENT_METHODS_ADVERTISING).filter(
        (advertisingId) => PAYMENT_ADVERTISING_IDS.PAYPAL !== advertisingId,
      )
  }
}

/**
 * Take the vue component instance 'this' and construct and return the advertising options
 * TODO [PAYIN-2614] Remove this when the Paypal AB test is over
 * @param {import('vue').default} vueComponentInstance
 */
export const getAdvertisingOptionsFromComponent = (vueComponentInstance) => {
  return {
    rootGetters: vueComponentInstance.$store.getters,
  }
}

/**
 * @param {import('@payment').PaymentMethod[]} paymentMethods
 * @param {string} type See PAYMENT_ADVERTISING_TYPES
 * @param {PaymentAdvertisingOptions} advertisingOptions
 * @returns {import('../config/advertising').PaymentAdvertisingConfig | null}
 */
const findAvailablePaymentAdvertising = (
  paymentMethods,
  type,
  advertisingOptions,
) => {
  const advertisingIds = filterABTestedPaymentAdvertising(
    paymentMethods,
    advertisingOptions,
  )
  // Using for-const-of instead of double `.find`
  // eslint-disable-next-line no-restricted-syntax
  for (const advertisingId of advertisingIds) {
    const advertisingConfig = PAYMENT_METHODS_ADVERTISING[advertisingId]
    if (advertisingConfig[type]) {
      const { isAvailable, forMethods } =
        advertisingConfig.isAvailableFor(paymentMethods)
      if (isAvailable) {
        return {
          ...advertisingConfig,
          id: advertisingId,
          methods: forMethods,
        }
      }
    }
  }

  return null
}

/**
 * @param {PaymentMethod[]} paymentMethods
 * @param {string} type See PAYMENT_ADVERTISING_TYPES
 * @param {PaymentAdvertisingOptions} advertisingOptions
 * @returns {null | ({ variant: string, method: PaymentMethod, methodName: string } & Object)}
 */
function paymentAdvertising(paymentMethods, type, advertisingOptions) {
  const config = findAvailablePaymentAdvertising(
    paymentMethods,
    type,
    advertisingOptions,
  )

  if (!config) {
    return null
  }

  const method = config.methods[0]

  return {
    variant: type,
    method,
    methodName: paymentMethodName(method.bmCode),
    ...config[type],
  }
}

/**
 * @param {PaymentMethod[]} paymentMethods
 * @param {PaymentAdvertisingOptions} advertisingOptions
 * @return {(PaymentAdvertisingProductConfig & { variant: string, method: PaymentMethod }) | null}
 */
export const cartPaymentAdvertising = (paymentMethods, advertisingOptions) =>
  paymentAdvertising(
    paymentMethods,
    PAYMENT_ADVERTISING_TYPES.CART,
    advertisingOptions,
  )

/**
 * @param {PaymentMethod[]} paymentMethods
 * @param {PaymentAdvertisingOptions} advertisingOptions
 * @return {(PaymentAdvertisingLandingConfig & { variant: string, method: PaymentMethod }) | null}
 */
export const landingPaymentAdvertising = (paymentMethods, advertisingOptions) =>
  paymentAdvertising(
    paymentMethods.filter(isPaymentMethodEnabled),
    PAYMENT_ADVERTISING_TYPES.LANDING,
    advertisingOptions,
  )

/**
 * @param {PaymentMethod[]} paymentMethods
 * @param {PaymentAdvertisingOptions} advertisingOptions
 * @returns {(PaymentAdvertisingSearchConfig & { variant: string, method: PaymentMethod }) | null}
 */
export const searchPaymentAdvertising = (paymentMethods, advertisingOptions) =>
  paymentAdvertising(
    paymentMethods.filter(isPaymentMethodEnabled),
    PAYMENT_ADVERTISING_TYPES.SEARCH,
    advertisingOptions,
  )

/**
 * @param {PaymentMethod[]} paymentMethods
 * @param {PaymentAdvertisingOptions} advertisingOptions
 * @returns {boolean}
 */
export const hasSearchPaymentAdvertising = (
  paymentMethods,
  advertisingOptions,
) => searchPaymentAdvertising(paymentMethods, advertisingOptions) !== null

/**
 * @param {PaymentMethod[]} paymentMethods
 * @param {string} networkId
 * @returns {{ match: PaymentMethod[], rest: PaymentMethod[] }}
 */
const splitPaymentMethodsByNetworkId = (paymentMethods, networkId) =>
  paymentMethods.reduce(
    ({ match, rest }, paymentMethod) =>
      paymentMethod.networks.includes(networkId)
        ? { match: [...match, paymentMethod], rest }
        : { match, rest: [...rest, paymentMethod] },
    { match: [], rest: [] },
  )

/**
 * @param {PaymentMethod[]} paymentMethods
 * @param {PaymentAdvertisingOptions} advertisingOptions
 * @returns {{
 *   advertised: null | (PaymentAdvertisingProductConfig & { variant: string, methods: PaymentMethod[], networkId: string }),
 *   rest: PaymentMethod[],
 * }}
 */
export function productPaymentAdvertising(paymentMethods, advertisingOptions) {
  const enabledMethods = paymentMethods.filter(isPaymentMethodEnabled)

  const advertisingConfig = findAvailablePaymentAdvertising(
    enabledMethods,
    PAYMENT_ADVERTISING_TYPES.PRODUCT,
    advertisingOptions,
  )
  const paymentMethod = advertisingConfig && advertisingConfig.methods[0]
  const networkId = paymentMethod && paymentMethod.networks[0]

  if (!networkId) {
    return {
      advertised: null,
      rest: enabledMethods,
    }
  }

  const { match, rest } = splitPaymentMethodsByNetworkId(
    enabledMethods,
    networkId,
  )

  return {
    advertised: {
      variant: PAYMENT_ADVERTISING_TYPES.PRODUCT,
      methods: match,
      networkId,
      ...advertisingConfig[PAYMENT_ADVERTISING_TYPES.PRODUCT],
    },
    rest,
  }
}
